import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Trash2,
  Reply,
  ReplyAll,
  Forward,
  Folder,
  Paperclip,
  Download,
  FileText,
  Tag,
  MailOpen,
  Edit3,
  Archive,
  Loader2,
  AlertTriangle,
  MoreHorizontal,
} from "lucide-react";
import CalendarInvitationCard from "./CalendarInvitationCard";
import { ErrorBoundary } from "./ErrorBoundary";
import cloudflareApi, { Email as CloudflareEmail } from "../lib/cloudflare-api";

const fastSpring = { type: "spring" as const, stiffness: 500, damping: 30 };

interface AttachmentDetail {
  id: string;
  filename: string;
  content_type: string;
  size: number;
}

interface EmailDetail {
  id: string;
  sender: string;
  subject: string;
  body: string;
  date: string;
  attachments: string[];
  attachmentDetails?: AttachmentDetail[];
  to?: string;
  cc?: string;
  bcc?: string;
  tags?: string[];
  is_draft?: boolean;
  read?: boolean;
  folder?: string;
  thread_id?: string;
  is_html?: boolean;
  has_calendar_invitation?: boolean;
  calendar_event_id?: string;
}

interface MessageViewProps {
  emailId: string;
  onClose: () => void;
  onDelete: () => void;
  onReply: (mode: 'reply' | 'replyAll' | 'forward', emailData: EmailDetail) => void;
  onEditDraft?: (draftData: EmailDetail) => void;
  onNavigateToEmail?: (emailId: string) => void;
}

const sanitizeHtml = (html: string | null | undefined): string => {
  if (!html || typeof html !== 'string') return '';

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const dangerousTags = ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'style', 'link', 'meta'];
    dangerousTags.forEach(tag => {
      const elements = Array.from(doc.getElementsByTagName(tag));
      elements.forEach(el => {
        el.parentNode?.removeChild(el);
      });
    });

    const allElements = doc.body.getElementsByTagName('*');
    for (let i = 0; i < allElements.length; i++) {
      const el = allElements[i];

      const attrs = Array.from(el.attributes);
      for (const attr of attrs) {
        if (attr.name.startsWith('on') ||
          attr.value.toLowerCase().includes('javascript:')) {
          el.removeAttribute(attr.name);
        }
      }

      if (el.hasAttribute('href') && el.getAttribute('href')?.toLowerCase().includes('javascript:')) {
        el.removeAttribute('href');
      }
      if (el.hasAttribute('src') && el.getAttribute('src')?.toLowerCase().includes('javascript:')) {
        el.removeAttribute('src');
      }
    }

    const images = doc.getElementsByTagName('img');
    const imagesToRemove: Element[] = [];
    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      const width = img.getAttribute('width');
      const height = img.getAttribute('height');
      const src = img.getAttribute('src') || '';

      if ((width === '1' && height === '1') ||
        /track|pixel|beacon|wf\/open|click/i.test(src)) {
        imagesToRemove.push(img);
      }
    }
    imagesToRemove.forEach(img => img.parentNode?.removeChild(img));

    return doc.body.innerHTML;
  } catch (err) {
    console.error('Error sanitizing HTML:', err);
    return '<p>Error al procesar el contenido del correo.</p>';
  }
};

const safeJsonParse = (str: string | null | undefined, fallback: unknown = []): unknown => {
  if (!str || typeof str !== 'string') return fallback;
  try {
    return JSON.parse(str);
  } catch {
    console.error('Error parsing JSON:', str);
    return fallback;
  }
};

type OverflowView = 'main' | 'folder' | 'tag';

export default function MessageView({ emailId, onClose, onDelete, onReply, onEditDraft, onNavigateToEmail }: MessageViewProps) {
  const [email, setEmail] = useState<EmailDetail | null>(null);
  const [customFolders, setCustomFolders] = useState<string[]>([]);
  const [allTags] = useState<string[]>([]);
  const [downloadingAttachments, setDownloadingAttachments] = useState<Set<string>>(new Set());
  const [newTag, setNewTag] = useState("");
  const [threadEmails, setThreadEmails] = useState<EmailDetail[]>([]);

  // New layout states
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const [overflowView, setOverflowView] = useState<OverflowView>('main');
  const [showDetails, setShowDetails] = useState(false);
  const [expandedThreadIds, setExpandedThreadIds] = useState<Set<string>>(new Set());

  const overflowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadEmail();
    loadCustomFolders();
  }, [emailId]);

  // Close overflow on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target as Node)) {
        setShowOverflowMenu(false);
      }
    };

    if (showOverflowMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showOverflowMenu]);

  const loadCustomFolders = async () => {
    try {
      const result = await cloudflareApi.getFolders();
      setCustomFolders(result.folders);
    } catch (error) {
      console.error("Error loading folders:", error);
    }
  };

  const transformEmail = (e: CloudflareEmail): EmailDetail => {
    try {
      let dateStr: string;
      try {
        dateStr = new Date(e.timestamp).toISOString();
      } catch {
        dateStr = new Date().toISOString();
      }

      return {
        id: e.id || '',
        sender: e.sender || '',
        subject: e.subject || '(Sin asunto)',
        body: e.body_html || e.body_plain || '',
        date: dateStr,
        attachments: e.attachments?.map(a => a.filename) || [],
        attachmentDetails: e.attachments?.map(a => ({
          id: a.id,
          filename: a.filename,
          content_type: a.content_type,
          size: a.size,
        })) || [],
        to: e.to_field || e.recipient || '',
        cc: e.cc_field || undefined,
        bcc: e.bcc_field || undefined,
        tags: safeJsonParse(e.tags, []) as string[],
        is_draft: e.is_draft === 1,
        read: e.is_read === 1,
        folder: e.folder || 'inbox',
        thread_id: e.thread_id || undefined,
        is_html: e.is_html === 1 || !!e.body_html,
        has_calendar_invitation: e.has_calendar_invitation === 1,
        calendar_event_id: e.calendar_event_id || undefined,
      };
    } catch (err) {
      console.error('Error transforming email:', err);
      return {
        id: e?.id || 'error',
        sender: e?.sender || 'Error',
        subject: 'Error al cargar el correo',
        body: 'No se pudo cargar el contenido del correo.',
        date: new Date().toISOString(),
        attachments: [],
        attachmentDetails: [],
        to: '',
        tags: [],
        is_draft: false,
        read: false,
        folder: 'inbox',
        is_html: false,
        has_calendar_invitation: false,
        calendar_event_id: undefined,
      };
    }
  };

  const loadEmail = async () => {
    try {
      const result = await cloudflareApi.getEmail(emailId);
      const emailDetail = transformEmail(result.email);
      setEmail(emailDetail);

      if (!emailDetail.is_draft && !emailDetail.read) {
        try {
          await cloudflareApi.updateEmailStatus(emailId, { is_read: true });
        } catch (error) {
          console.error("Error marking as read:", error);
        }
      }

      try {
        const threadResult = await cloudflareApi.getEmailThread(emailId);
        if (threadResult.emails && threadResult.emails.length > 0) {
          const threadDetails: EmailDetail[] = threadResult.emails.map(e => {
            let dateStr: string;
            try {
              dateStr = new Date(e.timestamp).toISOString();
            } catch {
              dateStr = new Date().toISOString();
            }

            return {
              id: e.id || '',
              sender: e.sender || '',
              subject: e.subject || '(Sin asunto)',
              body: e.body_plain || '',
              date: dateStr,
              attachments: [],
              read: e.is_read === 1,
              folder: e.folder || 'inbox',
            };
          });
          setThreadEmails(threadDetails);
        } else {
          setThreadEmails([]);
        }
      } catch (error) {
        console.error("Error loading thread:", error);
        setThreadEmails([]);
      }
    } catch (error) {
      console.error("Error loading email:", error);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDownloadAttachment = async (attachment: AttachmentDetail) => {
    if (downloadingAttachments.has(attachment.id)) return;

    try {
      setDownloadingAttachments(prev => new Set(prev).add(attachment.id));

      const blob = await cloudflareApi.downloadAttachment(attachment.id);
      const downloadUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error downloading attachment:", error);
      alert(`Error al descargar: ${error}`);
    } finally {
      setDownloadingAttachments(prev => {
        const next = new Set(prev);
        next.delete(attachment.id);
        return next;
      });
    }
  };

  const handleOpenAttachment = async (attachmentPath: string) => {
    const attachment = email?.attachmentDetails?.find(a => a.filename === attachmentPath);
    if (attachment) {
      await handleDownloadAttachment(attachment);
    } else {
      console.warn("Attachment details not found for:", attachmentPath);
      alert(`El archivo "${attachmentPath}" no está disponible. Los adjuntos antiguos no son accesibles en la versión web.`);
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      if (!dateStr) return 'Fecha desconocida';
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'Fecha inválida';
      return date.toLocaleString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return 'Fecha desconocida';
    }
  };

  const getInitials = (name: string) => {
    try {
      if (!name || typeof name !== 'string' || name.trim() === '') return '??';
      const parts = name.split(/[@\s]/);
      if (parts.length >= 2 && parts[0] && parts[1] && parts[0][0] && parts[1][0]) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      if (name.length >= 2) {
        return name.substring(0, 2).toUpperCase();
      }
      return name.substring(0, 1).toUpperCase() || '?';
    } catch {
      return '??';
    }
  };

  const getFileName = (path: string) => {
    return path.split('/').pop() || path;
  };

  const getSenderName = (sender: string) => {
    return (sender || '').split('<')[0].trim() || (sender || '').split('@')[0] || 'Desconocido';
  };

  const handleDelete = async () => {
    if (email?.folder === "trash") {
      if (!confirm("¿Eliminar permanentemente este correo?")) return;
      try {
        await cloudflareApi.deleteEmail(emailId);
        onDelete();
      } catch (error) {
        alert(`Error al eliminar: ${error}`);
      }
    } else {
      try {
        await cloudflareApi.updateEmailStatus(emailId, { folder: "trash" });
        onDelete();
      } catch (error) {
        alert(`Error: ${error}`);
      }
    }
  };

  const handleMarkAsUnread = async () => {
    try {
      await cloudflareApi.updateEmailStatus(emailId, { is_read: false });
      alert("Marcado como no leído");
      onClose();
    } catch (error) {
      alert(`Error: ${error}`);
    }
  };

  const handleEditDraft = () => {
    if (email && onEditDraft) {
      onEditDraft(email);
    }
  };

  const handleAddTag = async (tag: string) => {
    try {
      const currentTags = email?.tags || [];
      if (!currentTags.includes(tag)) {
        const newTags = [...currentTags, tag];
        await cloudflareApi.updateEmailStatus(emailId, { tags: newTags });
        await loadEmail();
        setNewTag("");
      }
    } catch (error) {
      alert(`Error: ${error}`);
    }
  };

  const handleRemoveTag = async (tag: string) => {
    try {
      const currentTags = email?.tags || [];
      const newTags = currentTags.filter(t => t !== tag);
      await cloudflareApi.updateEmailStatus(emailId, { tags: newTags });
      await loadEmail();
    } catch (error) {
      alert(`Error: ${error}`);
    }
  };

  const handleRestoreFromTrash = async () => {
    try {
      await cloudflareApi.updateEmailStatus(emailId, { folder: "inbox" });
      onDelete();
    } catch (error) {
      alert(`Error: ${error}`);
    }
  };

  const handleReply = () => {
    if (email) onReply('reply', email);
  };

  const handleReplyAll = () => {
    if (email) onReply('replyAll', email);
  };

  const handleForward = () => {
    if (email) onReply('forward', email);
  };

  const handleMoveToFolder = async (folder: string) => {
    try {
      await cloudflareApi.updateEmailStatus(emailId, { folder });
      setShowOverflowMenu(false);
      onDelete();
    } catch (error) {
      alert(`Error: ${error}`);
    }
  };

  const toggleThreadExpand = (id: string) => {
    setExpandedThreadIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const actionBtnStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: 'var(--text-tertiary)',
    cursor: 'pointer',
    padding: '6px',
    borderRadius: 'var(--radius-md, 8px)',
    display: 'flex',
    alignItems: 'center',
    transition: 'color 150ms ease, background-color 150ms ease',
  };

  const menuItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    padding: '8px 12px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    color: 'var(--text-primary)',
    textAlign: 'left',
    borderRadius: 'var(--radius-sm, 4px)',
    transition: 'background-color 150ms ease',
  };

  if (!email) {
    return (
      <div className="message-view">
        <div className="message-header">
          <div className="message-header-left">
            <button onClick={onClose} style={actionBtnStyle}>
              <ChevronLeft size={20} />
            </button>
          </div>
        </div>
        <div className="loading">Cargando...</div>
      </div>
    );
  }

  const hasAttachments = (email.attachmentDetails?.length || 0) > 0 || email.attachments.length > 0;
  const attachmentCount = email.attachmentDetails?.length || email.attachments.length;

  return (
    <div
      className="message-view"
      key={emailId}
      style={{ opacity: 0, animation: 'msgFadeIn 200ms ease forwards' }}
    >
      {/* Top Bar: back + subject + reply + overflow */}
      <div className="message-header" style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        <button onClick={onClose} style={actionBtnStyle} title="Volver">
          <ChevronLeft size={20} />
        </button>
        <h1 style={{
          flex: 1,
          fontSize: '14px',
          fontWeight: 600,
          margin: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: 'var(--text-primary)',
        }}>
          {email.subject}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
          {email.is_draft ? (
            <button onClick={handleEditDraft} style={actionBtnStyle} title="Editar borrador">
              <Edit3 size={18} />
            </button>
          ) : (
            <>
              <button onClick={handleReply} style={actionBtnStyle} title="Responder">
                <Reply size={18} />
              </button>
              <div ref={overflowRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => { setShowOverflowMenu(!showOverflowMenu); setOverflowView('main'); }}
                  style={actionBtnStyle}
                  title="Más acciones"
                >
                  <MoreHorizontal size={18} />
                </button>
                <AnimatePresence>
                  {showOverflowMenu && (
                    <motion.div
                      className="message-dropdown-menu"
                      initial={{ opacity: 0, y: -8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.96 }}
                      transition={fastSpring}
                      style={{ right: 0, minWidth: '200px' }}
                    >
                      {overflowView === 'main' && (
                        <>
                          <button style={menuItemStyle} onClick={() => { handleReplyAll(); setShowOverflowMenu(false); }}>
                            <ReplyAll size={16} /> Responder a todos
                          </button>
                          <button style={menuItemStyle} onClick={() => { handleForward(); setShowOverflowMenu(false); }}>
                            <Forward size={16} /> Reenviar
                          </button>
                          <div className="message-dropdown-divider" />
                          <button style={menuItemStyle} onClick={() => setOverflowView('folder')}>
                            <Folder size={16} /> Mover a carpeta
                          </button>
                          <button style={menuItemStyle} onClick={() => setOverflowView('tag')}>
                            <Tag size={16} /> Etiquetas
                          </button>
                          {email.read && !email.is_draft && (
                            <button style={menuItemStyle} onClick={() => { handleMarkAsUnread(); setShowOverflowMenu(false); }}>
                              <MailOpen size={16} /> Marcar como no leído
                            </button>
                          )}
                          <div className="message-dropdown-divider" />
                          {email.folder === "trash" ? (
                            <>
                              <button style={menuItemStyle} onClick={() => { handleRestoreFromTrash(); setShowOverflowMenu(false); }}>
                                <Archive size={16} /> Restaurar
                              </button>
                              <button style={{ ...menuItemStyle, color: 'var(--danger)' }} onClick={() => { handleDelete(); setShowOverflowMenu(false); }}>
                                <Trash2 size={16} /> Eliminar permanentemente
                              </button>
                            </>
                          ) : (
                            <button style={{ ...menuItemStyle, color: 'var(--danger)' }} onClick={() => { handleDelete(); setShowOverflowMenu(false); }}>
                              <Trash2 size={16} /> Mover a papelera
                            </button>
                          )}
                        </>
                      )}
                      {overflowView === 'folder' && (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px' }}>
                            <button onClick={() => setOverflowView('main')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-secondary)', display: 'flex' }}>
                              <ChevronLeft size={16} />
                            </button>
                            <span style={{ fontWeight: 600, fontSize: '12px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Mover a carpeta</span>
                          </div>
                          <div className="message-dropdown-divider" />
                          <button style={menuItemStyle} onClick={() => handleMoveToFolder("inbox")}>Recibidos</button>
                          <button style={menuItemStyle} onClick={() => handleMoveToFolder("sent")}>Enviados</button>
                          <button style={menuItemStyle} onClick={() => handleMoveToFolder("spam")}>Spam</button>
                          {customFolders.length > 0 && (
                            <>
                              <div className="message-dropdown-divider" />
                              {customFolders.map(folder => (
                                <button key={folder} style={menuItemStyle} onClick={() => handleMoveToFolder(folder)}>{folder}</button>
                              ))}
                            </>
                          )}
                        </>
                      )}
                      {overflowView === 'tag' && (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px' }}>
                            <button onClick={() => setOverflowView('main')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-secondary)', display: 'flex' }}>
                              <ChevronLeft size={16} />
                            </button>
                            <span style={{ fontWeight: 600, fontSize: '12px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Etiquetas</span>
                          </div>
                          <div className="message-dropdown-divider" />
                          {email.tags && email.tags.length > 0 && (
                            <>
                              {email.tags.map(tag => (
                                <div key={tag} className="message-tag-item">
                                  <span>{tag}</span>
                                  <button onClick={() => handleRemoveTag(tag)}>×</button>
                                </div>
                              ))}
                              <div className="message-dropdown-divider" />
                            </>
                          )}
                          <div className="message-new-tag">
                            <input
                              type="text"
                              placeholder="Nueva etiqueta"
                              value={newTag}
                              onChange={(e) => setNewTag(e.target.value)}
                              onKeyPress={(e) => e.key === "Enter" && newTag && handleAddTag(newTag)}
                            />
                            <button onClick={() => newTag && handleAddTag(newTag)}>+</button>
                          </div>
                          {allTags.length > 0 && (
                            <>
                              <div className="message-dropdown-divider" />
                              <div style={{ padding: '4px 12px', fontSize: '11px', color: 'var(--text-tertiary)', fontWeight: 600 }}>Existentes</div>
                              {allTags.filter(t => !email.tags?.includes(t)).map(tag => (
                                <button key={tag} style={menuItemStyle} onClick={() => handleAddTag(tag)}>{tag}</button>
                              ))}
                            </>
                          )}
                        </>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Email Content */}
      <div className="message-content">
        <ErrorBoundary
          fallback={
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <AlertTriangle size={48} style={{ color: 'var(--warning)' }} />
              <h3>Error al mostrar el correo</h3>
              <p>No se pudo renderizar el contenido de este correo.</p>
              <button onClick={onClose} style={{ marginTop: '16px', padding: '8px 16px' }}>
                Volver
              </button>
            </div>
          }
        >
          <div style={{ maxWidth: '680px', margin: '0 auto', padding: '24px 20px' }}>
            {/* Sender Section */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '20px' }}>
              <div className="message-from-avatar" style={{
                width: '40px',
                height: '40px',
                minWidth: '40px',
                fontSize: '14px',
                flexShrink: 0,
              }}>
                {getInitials(email.sender || '')}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px' }}>
                  <div className="message-from-name" style={{ fontSize: '15px', fontWeight: 600 }}>
                    {getSenderName(email.sender)}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', flexShrink: 0, whiteSpace: 'nowrap' }}>
                    {formatDate(email.date)}
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                  {email.sender || ''}
                </div>

                {/* Collapsible To/CC/BCC */}
                {(email.to || email.cc || email.bcc) && (
                  <>
                    {!showDetails ? (
                      <button
                        onClick={() => setShowDetails(true)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          fontSize: '12px',
                          color: 'var(--primary)',
                          marginTop: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        Mostrar detalles <ChevronDown size={12} />
                      </button>
                    ) : (
                      <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                        <div style={{ marginBottom: '2px' }}>
                          <span style={{ color: 'var(--text-tertiary)' }}>Para: </span>
                          {email.to || "Yo"}
                        </div>
                        {email.cc && (
                          <div style={{ marginBottom: '2px' }}>
                            <span style={{ color: 'var(--text-tertiary)' }}>CC: </span>
                            {email.cc}
                          </div>
                        )}
                        {email.bcc && (
                          <div style={{ marginBottom: '2px' }}>
                            <span style={{ color: 'var(--text-tertiary)' }}>BCC: </span>
                            {email.bcc}
                          </div>
                        )}
                        <button
                          onClick={() => setShowDetails(false)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            fontSize: '12px',
                            color: 'var(--primary)',
                            marginTop: '2px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          Ocultar detalles <ChevronUp size={12} />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Tags */}
            {email.tags && Array.isArray(email.tags) && email.tags.length > 0 && (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px',
                marginBottom: '16px',
              }}>
                {email.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '2px 8px',
                      background: 'var(--primary-light)',
                      color: 'var(--primary)',
                      borderRadius: 'var(--radius-sm, 4px)',
                      fontSize: '12px',
                      fontWeight: 500,
                    }}
                  >
                    <Tag size={12} />
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Important Badge */}
            {(email.subject || '').toLowerCase().includes('important') && (
              <div style={{
                padding: '8px 12px',
                background: 'rgba(234, 179, 8, 0.1)',
                border: '1px solid rgba(234, 179, 8, 0.2)',
                borderRadius: 'var(--radius-md, 8px)',
                fontSize: '13px',
                color: 'var(--warning)',
                marginBottom: '16px',
              }}>
                ⚠️ Este mensaje es muy importante.
              </div>
            )}

            {/* Calendar Invitation */}
            <ErrorBoundary
              fallback={
                <div style={{ margin: '16px 0', textAlign: 'center' }}>
                  <AlertTriangle size={32} style={{ color: 'var(--warning)' }} />
                  <h3>Error al cargar invitación</h3>
                </div>
              }
            >
              {email.has_calendar_invitation && !email.calendar_event_id && (
                <CalendarInvitationCard
                  emailId={email.id}
                  emailBody={email.body}
                  onAccept={() => { loadEmail(); }}
                  onDecline={() => { loadEmail(); }}
                />
              )}

              {email.calendar_event_id && (
                <div className="calendar-invitation-card accepted">
                  <div className="invitation-content">
                    <h3>✅ Ya aceptaste esta invitación</h3>
                    <p>El evento está en tu calendario</p>
                  </div>
                </div>
              )}
            </ErrorBoundary>

            {/* Email Body */}
            <ErrorBoundary
              fallback={
                <div style={{ margin: '16px 0', textAlign: 'center' }}>
                  <AlertTriangle size={32} style={{ color: 'var(--warning)' }} />
                  <h3>Error al mostrar contenido</h3>
                </div>
              }
            >
              <div
                className="message-body"
                dangerouslySetInnerHTML={{
                  __html: email.is_html
                    ? sanitizeHtml(email.body)
                    : (email.body || '').replace(/\n/g, '<br>')
                }}
              />
            </ErrorBoundary>

            {/* Attachments - Horizontal Scrollable Row */}
            {hasAttachments && (
              <div style={{ marginTop: '24px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                }}>
                  <Paperclip size={14} style={{ color: 'var(--primary)' }} />
                  <span>Adjuntos</span>
                  <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>
                    {attachmentCount} archivo{attachmentCount !== 1 ? 's' : ''}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  overflowX: 'auto',
                  gap: '8px',
                  paddingBottom: '4px',
                }}>
                  {email.attachmentDetails && email.attachmentDetails.length > 0 ? (
                    email.attachmentDetails.map((att) => {
                      const isDownloading = downloadingAttachments.has(att.id);
                      return (
                        <div
                          key={att.id}
                          onClick={() => !isDownloading && handleDownloadAttachment(att)}
                          style={{
                            minWidth: '160px',
                            maxWidth: '200px',
                            padding: '10px 12px',
                            border: '1px solid var(--border)',
                            borderRadius: 'var(--radius-md, 8px)',
                            cursor: isDownloading ? 'wait' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'border-color 150ms ease, background-color 150ms ease',
                            background: 'var(--bg-secondary, transparent)',
                            opacity: isDownloading ? 0.6 : 1,
                          }}
                        >
                          <div style={{ color: 'var(--primary)', flexShrink: 0 }}>
                            {isDownloading ? <Loader2 size={18} className="spinning" /> : <FileText size={18} />}
                          </div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: '12px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {att.filename}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                              {formatFileSize(att.size)}
                            </div>
                          </div>
                          <Download size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                        </div>
                      );
                    })
                  ) : (
                    email.attachments.map((att, idx) => (
                      <div
                        key={idx}
                        onClick={() => handleOpenAttachment(att)}
                        style={{
                          minWidth: '160px',
                          maxWidth: '200px',
                          padding: '10px 12px',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-md, 8px)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          transition: 'border-color 150ms ease',
                          background: 'var(--bg-secondary, transparent)',
                        }}
                      >
                        <FileText size={18} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: '12px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {getFileName(att)}
                          </div>
                        </div>
                        <Download size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Thread / Conversation - Collapsible */}
            {threadEmails.length > 0 && (
              <div style={{
                marginTop: '24px',
                borderTop: '1px solid var(--border)',
                paddingTop: '16px',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                }}>
                  <Reply size={14} style={{ color: 'var(--primary)' }} />
                  <span>Conversación ({threadEmails.length} mensaje{threadEmails.length !== 1 ? 's' : ''})</span>
                </div>
                <div>
                  {threadEmails.map((threadEmail) => {
                    const isExpanded = expandedThreadIds.has(threadEmail.id);
                    const isActive = threadEmail.id === emailId;
                    return (
                      <div
                        key={threadEmail.id || Math.random()}
                        style={{
                          borderBottom: '1px solid var(--border)',
                          background: isActive ? 'var(--bg-hover)' : 'transparent',
                          borderRadius: 'var(--radius-sm, 4px)',
                          transition: 'background-color 150ms ease',
                        }}
                      >
                        <div
                          onClick={() => {
                            if (!isActive) {
                              toggleThreadExpand(threadEmail.id);
                            }
                            if (threadEmail.id !== emailId && onNavigateToEmail) {
                              onNavigateToEmail(threadEmail.id);
                            }
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 8px',
                            cursor: 'pointer',
                          }}
                        >
                          <div className="message-thread-avatar" style={{ width: '28px', height: '28px', fontSize: '10px', flexShrink: 0 }}>
                            {getInitials(threadEmail.sender || '')}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ fontWeight: 500, fontSize: '13px', color: 'var(--text-primary)' }}>
                              {getSenderName(threadEmail.sender)}
                            </span>
                          </div>
                          <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', flexShrink: 0, whiteSpace: 'nowrap' }}>
                            {formatDate(threadEmail.date)}
                          </span>
                          {!isActive && (
                            isExpanded
                              ? <ChevronUp size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                              : <ChevronDown size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                          )}
                        </div>
                        {isExpanded && !isActive && (
                          <div style={{
                            padding: '0 8px 12px 44px',
                            fontSize: '13px',
                            color: 'var(--text-secondary)',
                            lineHeight: 1.5,
                          }}>
                            {(threadEmail.body || '').substring(0, 300)}
                            {(threadEmail.body || '').length > 300 && '...'}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </ErrorBoundary>
      </div>

      <style>{`
        @keyframes msgFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
